export type CaseType = 'service' | 'billing' | 'other';
export type CaseOrigin = 'phone' | 'sms' | 'email' | 'web' | 'staff';
export type CaseStatus =
  | 'new'
  | 'in_progress'
  | 'awaiting_end_user'
  | 'awaiting_upstream_carrier'
  | 'awaiting_nbn_appointment'
  | 'awaiting_supa_appointment'
  | 'escalated'
  | 'on_hold'
  | 'billing_system_update_required'
  | 'completed';
export type CasePriority = 'low' | 'medium' | 'high';

/** case_origin, status, priority are strings to support API-driven options (Settings → Case Field Options) */
export interface Case {
  id: string;
  case_number: string;
  account_id: string;
  subject: string;
  description?: string | null;
  case_type: CaseType;
  case_reason?: string | null;
  case_origin: string;
  contact_id?: string | null;
  account_service_id?: string | null;
  account_service_display?: string | null;
  account_asset_id?: string | null;
  account_asset_display?: string | null;
  status: string;
  priority: string;
  assignee_id?: string | null;
  assignee_name?: string | null;
  due_by?: string | null;
  first_response_at?: string | null;
  resolved_at?: string | null;
  merged_into_id?: string | null;
  merged_into_case_number?: string | null;
  created_at: string;
  updated_at?: string | null;
  created_by_id?: string | null;
  account_display_name?: string | null;
  activity?: CaseActivity[];
  attachments?: CaseAttachment[];
  msp_use_hours?: boolean | null;
  msp_hours_reserved?: string | null;
}

export interface CaseActivity {
  id: string;
  case_id: string;
  activity_type: string;
  content?: string | null;
  metadata?: Record<string, unknown>;
  user_id?: string | null;
  user_name?: string | null;
  created_at: string;
}

export interface CaseAttachment {
  id: string;
  case_id: string;
  filename: string;
  content_type?: string | null;
  file_size_bytes?: number | null;
  uploaded_by_id?: string | null;
  created_at: string;
}

export interface CaseCreate {
  account_id: string;
  subject: string;
  description?: string | null;
  case_type: CaseType;
  case_reason?: string;
  case_origin: string;
  contact_id?: string;
  account_service_id?: string;
  account_asset_id?: string;
  status: string;
  priority: string;
  assignee_id?: string;
  due_by?: string;
  msp_use_hours?: boolean;
  msp_hours?: number | null;
}

export interface CaseUpdate {
  subject?: string;
  description?: string;
  case_type?: CaseType;
  case_reason?: string;
  case_origin?: string;
  contact_id?: string;
  account_service_id?: string;
  account_asset_id?: string;
  status?: string;
  priority?: string;
  assignee_id?: string;
  due_by?: string;
  msp_use_hours?: boolean;
  msp_hours?: number | null;
}

export interface CaseFilters {
  account_id?: string;
  status?: string;
  exclude_status?: string;
  case_type?: string;
  assignee_id?: string;
  q?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: string;
}

export interface CaseListResponse {
  cases: Case[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface CaseTemplate {
  id: string;
  name: string;
  subject?: string | null;
  body: string;
  variables?: string[];
  from_email?: string | null;
  from_name?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

export const CASE_EMAIL_PLACEHOLDERS = [
  '{{case_number}}',
  '{{subject}}',
  '{{description}}',
  '{{account_name}}',
  '{{assignee_name}}',
  '{{status}}',
  '{{priority}}',
  '{{case_type}}',
  '{{case_reason}}',
  '{{case_origin}}',
];
export const BODY_PLACEHOLDER = '%%email_body%%';
